/**
 * @file api.ts
 * @brief Hooks for connecting AI apps (Claude, ChatGPT…) to Verblizr over MCP.
 *
 * @details
 * The consent page reads the pending request and approves or declines it;
 * the profile section lists Connected AI apps and revokes them. The pending
 * request token travels in a POST body, never a URL we call, so it does not
 * land in server logs.
 *
 * @references
 * - BACKEND/src/features/mcp/oauth.routes.mjs (oauthAppRouter)
 *
 * @author DES
 * @date 06-10-2026
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiDelete, apiGet, apiPost } from "@/shared/api/client";

const base = "/api/mcp";

export const mcpKeys = {
  request: (r: string) => ["mcp-oauth-request", r] as const,
  connections: ["mcp-connections"] as const,
};

const ConsentRequestSchema = z.object({
  appName: z.string(),
  appHost: z.string().nullable(),
  scope: z.string(),
  /** Z1 (27-10-2026): "zapier", "make" (M1) or "mcp"; absent on an older backend = MCP. */
  kind: z.string().optional(),
  scopes: z.array(z.string()).optional().default([]),
  /** Company switched AI connectors off (Phase 2): no Allow, show why. */
  blocked: z.boolean().optional().default(false),
  blockedMessage: z.string().optional(),
  account: z.object({ email: z.string().nullable().optional(), name: z.string().nullable().optional() }),
});
export type ConsentRequest = z.infer<typeof ConsentRequestSchema>;

const DecisionSchema = z.object({ redirectTo: z.string().url() });

const ConnectionSchema = z.object({
  id: z.string(),
  appName: z.string(),
  appHost: z.string().nullable(),
  // Z1/M1: Zapier and Make connections sit in the same list; shown apart from AI apps.
  kind: z.string().optional().default("mcp"),
  scopes: z.array(z.string()).optional().default([]),
  // B42/W32: true while Super Admin has paused this app for the platform.
  paused: z.boolean().optional().default(false),
  connectedAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export type AiConnection = z.infer<typeof ConnectionSchema>;

/** What the consent screen shows. Never retried: an expired request stays expired. */
export function useConsentRequest(request: string) {
  return useQuery({
    queryKey: mcpKeys.request(request),
    queryFn: async () => ConsentRequestSchema.parse(await apiPost(`${base}/oauth/request`, { request })),
    enabled: Boolean(request),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** Allow / Decline → the URL to send the browser back to the AI app. */
export function useConsentDecision() {
  return useMutation({
    mutationFn: async (vars: { request: string; allow: boolean }) =>
      DecisionSchema.parse(
        await apiPost(`${base}/oauth/${vars.allow ? "approve" : "deny"}`, { request: vars.request }),
      ).redirectTo,
  });
}

export function useAiConnections() {
  return useQuery({
    queryKey: mcpKeys.connections,
    queryFn: async () =>
      z.object({ connections: z.array(ConnectionSchema) }).parse(await apiGet(`${base}/connections`)).connections,
    staleTime: 30_000,
  });
}

export function useRevokeAiConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`${base}/connections/${encodeURIComponent(id)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: mcpKeys.connections }),
  });
}
